// components/reports/ImagesAnalyticsReport.js
import React, { useState, useEffect } from 'react';
import { getImagesAnalytics } from '../../services/reportService';

/**
 * Компонент отчета по аналитике изображений ценников
 * @returns {JSX.Element} - Компонент отчета
 */
const ImagesAnalyticsReport = () => {
  const [data, setData] = useState({
    totalPriceTags: 0,
    priceTagsWithImages: 0,
    totalImages: 0,
    avgImagesPerTag: 0,
    imageDistribution: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: ''
  });

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const analytics = await getImagesAnalytics(filters);
      setData(analytics);
    } catch (err) {
      setError('Не удалось загрузить аналитику по изображениям');
      console.error('Error loading images analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  // Обработчик применения фильтров
  const handleApplyFilters = () => {
    loadData();
  };

  // Обработчик сброса фильтров
  const handleResetFilters = () => {
    setFilters({ startDate: '', endDate: '' });
    setTimeout(() => loadData(), 100);
  };

  // Расчет процента для распределения
  const getPercentage = (count, total) => {
    if (total === 0) return 0;
    return Math.round((count / total) * 100);
  };

  // Получение цвета для бара в зависимости от количества изображений
  const getBarColor = (imageCount) => {
    if (imageCount === 0) return 'bg-red-400';
    if (imageCount === 1) return 'bg-yellow-400';
    if (imageCount <= 3) return 'bg-blue-400';
    if (imageCount <= 5) return 'bg-green-400';
    return 'bg-purple-400';
  };

  // Получение описания для количества изображений
  const getImageCountDescription = (imageCount) => {
    if (imageCount === 0) return 'Без изображений';
    if (imageCount === 1) return '1 изображение';
    if (imageCount <= 4) return `${imageCount} изображения`;
    return `${imageCount} изображений`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <svg className="h-12 w-12 text-red-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-600">{error}</p>
            <button
              onClick={loadData}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Повторить попытку
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Заголовок и фильтры */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Аналитика изображений</h2>
            <p className="text-gray-600">Статистика использования изображений в ценниках</p>
          </div>
        </div>

        {/* Фильтры */}
        <div className="border-t pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Дата начала
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Дата окончания
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Применить
              </button>
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Сбросить
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Общая статистика */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Всего ценников</p>
              <p className="text-2xl font-bold text-gray-900">{data.totalPriceTags.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">С изображениями</p>
              <p className="text-2xl font-bold text-gray-900">{data.priceTagsWithImages.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Всего изображений</p>
              <p className="text-2xl font-bold text-gray-900">{data.totalImages.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100">
              <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Среднее на ценник</p>
              <p className="text-2xl font-bold text-gray-900">{data.avgImagesPerTag}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Распределение количества изображений */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Распределение по количеству изображений</h3>
        
        {data.imageDistribution.length === 0 ? (
          <div className="text-center py-8">
            <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-gray-500">Данные о распределении отсутствуют</p>
          </div>
        ) : (
          <div className="space-y-4">
            {data.imageDistribution.map((item) => (
              <div key={item.imageCount} className="flex items-center">
                <div className="w-32 text-sm text-gray-600">
                  {getImageCountDescription(item.imageCount)}
                </div>
                <div className="flex-1 mx-4">
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className={`h-4 rounded-full ${getBarColor(item.imageCount)}`}
                      style={{ width: `${getPercentage(item.priceTagCount, data.totalPriceTags)}%` }}
                    ></div>
                  </div>
                </div>
                <div className="w-20 text-right">
                  <span className="text-sm font-medium text-gray-900">
                    {item.priceTagCount}
                  </span>
                  <span className="text-xs text-gray-500 ml-1">
                    ({getPercentage(item.priceTagCount, data.totalPriceTags)}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Рекомендации */}
      {data.totalPriceTags > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start">
            <svg className="h-5 w-5 text-blue-600 mt-0.5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-blue-900 font-medium mb-2">Рекомендации по качеству данных</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                {data.priceTagsWithImages / data.totalPriceTags < 0.8 && (
                  <li>• Рассмотрите возможность добавления изображений к ценникам без фотографий</li>
                )}
                {data.avgImagesPerTag < 2 && (
                  <li>• Среднее количество изображений можно увеличить для лучшего качества данных</li>
                )}
                {data.avgImagesPerTag >= 5 && (
                  <li>• Отличное покрытие изображениями! Система работает эффективно</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagesAnalyticsReport;